import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchActiveUsersComparisonStatsAPI,
  fetchTournamentRegistrationAnalyticsAPI,
  fetchUsersSinceDateStatsAPI,
  fetchUserRegistrationStatsAPI,
} from "./adminStatisticsAPI";

const initialState = {
  selected: {
    year: null,
  },
  currentYearCount: 0,
  previousYearCount: 0,
  totalUsers: 0,
  todayRegisteredCount: 0,
  todayActiveUsersCount: 0,
  monthlySeries: [],
  latestTournamentPlayersSeries: [],
  regionUsersSeries: [],
  osUsersSeries: [],
  fetchStatus: "idle",
  error: null,
  activeComparison: {
    selected: null,
    current: null,
    previous: null,
    change: null,
  },
  activeComparisonStatus: "idle",
  activeComparisonError: null,
  usersSinceDateStats: {
    selected: null,
    registeredUsersCount: 0,
    activeUsersCount: 0,
    activeDefinition: null,
  },
  usersSinceDateStatsStatus: "idle",
  usersSinceDateStatsError: null,
  tournamentRegistrationAnalytics: {
    selected: null,
    tournamentOptions: [],
    totalRegisteredUsers: 0,
    regionSeries: [],
    registrationAgeSeries: [],
    registrationAgeDefinition: null,
  },
  tournamentRegistrationAnalyticsStatus: "idle",
  tournamentRegistrationAnalyticsError: null,
};

export const fetchUserRegistrationStats = createAsyncThunk(
  "adminStatistics/fetchUserRegistrationStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchUserRegistrationStatsAPI(params);
      return response?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.errorKey || "errors.server.internalError",
      );
    }
  },
);

export const fetchUserRegistrationStatsSection = createAsyncThunk(
  "adminStatistics/fetchUserRegistrationStatsSection",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchUserRegistrationStatsAPI(params);
      return {
        section: String(params?.section || "all"),
        data: response?.data || {},
      };
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.errorKey || "errors.server.internalError",
      );
    }
  },
);

const applyFullRegistrationStatsPayload = (state, payload = {}) => {
  state.selected = {
    year: payload?.selected?.year || null,
  };
  state.currentYearCount = Number(payload?.currentYearCount || 0);
  state.previousYearCount = Number(payload?.previousYearCount || 0);
  state.totalUsers = Number(payload?.totalUsers || 0);
  state.todayRegisteredCount = Number(payload?.todayRegisteredCount || 0);
  state.todayActiveUsersCount = Number(payload?.todayActiveUsersCount || 0);
  state.monthlySeries = Array.isArray(payload?.monthlySeries)
    ? payload.monthlySeries
    : [];
  state.latestTournamentPlayersSeries = Array.isArray(
    payload?.latestTournamentPlayersSeries,
  )
    ? payload.latestTournamentPlayersSeries
    : [];
  state.regionUsersSeries = Array.isArray(payload?.regionUsersSeries)
    ? payload.regionUsersSeries
    : [];
  state.osUsersSeries = Array.isArray(payload?.osUsersSeries)
    ? payload.osUsersSeries
    : [];
};

const applyRegistrationStatsSectionPayload = (
  state,
  section,
  payload = {},
) => {
  switch (section) {
    case "monthly":
      state.selected = {
        year: payload?.selected?.year || state.selected?.year || null,
      };
      state.currentYearCount = Number(payload?.currentYearCount || 0);
      state.previousYearCount = Number(payload?.previousYearCount || 0);
      state.monthlySeries = Array.isArray(payload?.monthlySeries)
        ? payload.monthlySeries
        : [];
      return;
    case "latestTournamentPlayers":
      state.latestTournamentPlayersSeries = Array.isArray(
        payload?.latestTournamentPlayersSeries,
      )
        ? payload.latestTournamentPlayersSeries
        : [];
      return;
    case "regionUsers":
      state.regionUsersSeries = Array.isArray(payload?.regionUsersSeries)
        ? payload.regionUsersSeries
        : [];
      return;
    case "osUsers":
      state.osUsersSeries = Array.isArray(payload?.osUsersSeries)
        ? payload.osUsersSeries
        : [];
      return;
    default:
      applyFullRegistrationStatsPayload(state, payload);
  }
};

export const fetchActiveUsersComparisonStats = createAsyncThunk(
  "adminStatistics/fetchActiveUsersComparisonStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchActiveUsersComparisonStatsAPI(params);
      return response?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.errorKey || "errors.server.internalError",
      );
    }
  },
);

export const fetchUsersSinceDateStats = createAsyncThunk(
  "adminStatistics/fetchUsersSinceDateStats",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchUsersSinceDateStatsAPI(params);
      return response?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.errorKey || "errors.server.internalError",
      );
    }
  },
);

export const fetchTournamentRegistrationAnalytics = createAsyncThunk(
  "adminStatistics/fetchTournamentRegistrationAnalytics",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await fetchTournamentRegistrationAnalyticsAPI(params);
      return response?.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.errorKey || "errors.server.internalError",
      );
    }
  },
);

const adminStatisticsSlice = createSlice({
  name: "adminStatistics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserRegistrationStats.pending, (state) => {
        state.fetchStatus = "loading";
        state.error = null;
      })
      .addCase(fetchUserRegistrationStats.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded";
        applyFullRegistrationStatsPayload(state, action.payload || {});
      })
      .addCase(fetchUserRegistrationStats.rejected, (state, action) => {
        state.fetchStatus = "failed";
        state.error = action.payload || "errors.server.internalError";
      })
      .addCase(fetchUserRegistrationStatsSection.fulfilled, (state, action) => {
        applyRegistrationStatsSectionPayload(
          state,
          action.payload?.section,
          action.payload?.data || {},
        );
      })
      .addCase(fetchActiveUsersComparisonStats.pending, (state) => {
        state.activeComparisonStatus = "loading";
        state.activeComparisonError = null;
      })
      .addCase(fetchActiveUsersComparisonStats.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.activeComparisonStatus = "succeeded";
        state.activeComparison = {
          selected: payload?.selected || null,
          current: payload?.current || null,
          previous: payload?.previous || null,
          change: payload?.change || null,
        };
      })
      .addCase(fetchActiveUsersComparisonStats.rejected, (state, action) => {
        state.activeComparisonStatus = "failed";
        state.activeComparisonError =
          action.payload || "errors.server.internalError";
      })
      .addCase(fetchUsersSinceDateStats.pending, (state) => {
        state.usersSinceDateStatsStatus = "loading";
        state.usersSinceDateStatsError = null;
      })
      .addCase(fetchUsersSinceDateStats.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.usersSinceDateStatsStatus = "succeeded";
        state.usersSinceDateStats = {
          selected: payload?.selected || null,
          registeredUsersCount: Number(payload?.registeredUsersCount || 0),
          activeUsersCount: Number(payload?.activeUsersCount || 0),
          activeDefinition: payload?.activeDefinition || null,
        };
      })
      .addCase(fetchUsersSinceDateStats.rejected, (state, action) => {
        state.usersSinceDateStatsStatus = "failed";
        state.usersSinceDateStatsError =
          action.payload || "errors.server.internalError";
      })
      .addCase(fetchTournamentRegistrationAnalytics.pending, (state) => {
        state.tournamentRegistrationAnalyticsStatus = "loading";
        state.tournamentRegistrationAnalyticsError = null;
      })
      .addCase(fetchTournamentRegistrationAnalytics.fulfilled, (state, action) => {
        const payload = action.payload || {};
        state.tournamentRegistrationAnalyticsStatus = "succeeded";
        state.tournamentRegistrationAnalytics = {
          selected: payload?.selected || null,
          tournamentOptions: Array.isArray(payload?.tournamentOptions)
            ? payload.tournamentOptions
            : [],
          totalRegisteredUsers: Number(payload?.totalRegisteredUsers || 0),
          regionSeries: Array.isArray(payload?.regionSeries)
            ? payload.regionSeries
            : [],
          registrationAgeSeries: Array.isArray(payload?.registrationAgeSeries)
            ? payload.registrationAgeSeries
            : [],
          registrationAgeDefinition: payload?.registrationAgeDefinition || null,
        };
      })
      .addCase(fetchTournamentRegistrationAnalytics.rejected, (state, action) => {
        state.tournamentRegistrationAnalyticsStatus = "failed";
        state.tournamentRegistrationAnalyticsError =
          action.payload || "errors.server.internalError";
      });
  },
});

export default adminStatisticsSlice.reducer;
